import { ApiError } from '@/lib/api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'

export interface ToastErrorPayload {
    title: string
    description?: string
}

// Builds a sonner toast payload from any thrown error so users see the
// localized error + a trace ID they can quote when reporting. `fallback` is the
// friendly Vietnamese label for the action (e.g. "Xác nhận đơn hàng thất bại")
// used when the error doesn't carry a message of its own (network failure,
// non-API throw).
//
// The frontend owns error i18n: a mapped code wins over any backend text. A
// code not mapped yet keeps the backend `message` during the expand-contract
// transition (see docs/decisions/error-i18n-single-owner.md); drop that branch
// once every module is migrated.
export function formatApiError(
    err: unknown,
    fallback: string,
): ToastErrorPayload {
    if (err instanceof ApiError) {
        // A mapped code always wins. Otherwise keep the existing behaviour:
        // the server-supplied human message, or `fallback` when the API didn't
        // return one. NETWORK_ERROR carries the axios message (English), so it
        // must use the localized action label instead. Trace ID always
        // surfaces in description.
        const title =
            resolveApiErrorMessage(err.code, err.params) ||
            (err.code === 'NETWORK_ERROR' ? fallback : err.message || fallback)
        const parts: string[] = []
        if (err.code && err.code !== 'UNKNOWN_ERROR') parts.push(err.code)
        if (err.requestId)
            parts.push(m.error_incident_code({ code: err.requestId }))
        return {
            title,
            description: parts.length > 0 ? parts.join(' · ') : undefined,
        }
    }
    return { title: fallback }
}
